import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { ru, type Dictionary, type TranslationKey } from './ru';
import { kk } from './kk';

export type Locale = 'ru' | 'kk';

const DICTIONARIES: Record<Locale, Dictionary> = { ru, kk };
const STORAGE_KEY = 'wf.locale';

export const LOCALE_LABELS: Record<Locale, string> = {
  ru: 'Русский',
  kk: 'Қазақша',
};

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /**
   * Перевод по ключу. Поддерживает подстановку {name}:
   * t('common.page', { page: 2 }). Неизвестный ключ возвращается как есть —
   * это заметно на экране и не роняет приложение.
   */
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'ru' || stored === 'kk') return stored;
  } catch {
    /* приватный режим */
  }
  return 'ru';
}

export function I18nProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(readStoredLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    document.documentElement.lang = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* приватный режим */
    }
  }, []);

  const t = useCallback<I18nContextValue['t']>(
    (key, params) => {
      const dictionary = DICTIONARIES[locale];
      let value: string = dictionary[key] ?? ru[key] ?? key;
      if (params) {
        for (const [name, param] of Object.entries(params)) {
          value = value.replace(new RegExp(`\\{${name}\\}`, 'g'), String(param));
        }
      }
      return value;
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n должен вызываться внутри I18nProvider');
  return context;
}

/** Короткий доступ только к функции перевода. */
export function useT(): I18nContextValue['t'] {
  return useI18n().t;
}

export type { TranslationKey };
